import qrcode from 'qrcode-generator';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import type { NombreIcono } from '../componentes/iconos';
import { navegador } from '../logica/navegador';

/** Las carteras del móvil (6.8, G50) y «Guardar el QR» (G602) de la página del pase y de «Mis pases». */

export type Nota = readonly [NombreIcono, 'info' | 'espera' | 'error', string];

/** Las notas de la cartera: Apple sin `401` (G422): `400` «el enlace no es válido», `404` «no encontramos este pase». */
function notaCartera(cartera: 'apple' | 'google', e: unknown): Nota {
    const nombre = cartera === 'apple' ? 'No hemos podido añadirlo a Apple Wallet' : 'No hemos podido guardarlo en Google Wallet';
    if (e instanceof ErrorApi && e.estado === 400) {
        return ['link', 'error', `${nombre}: el enlace no es válido. Ábrelo otra vez desde el email o el mensaje.`];
    }
    if (e instanceof ErrorApi && e.estado === 404) {
        return ['search', 'error', `${nombre}: no encontramos este pase.`];
    }
    return ['circle-alert', 'error', `${nombre}. Comprueba la conexión y prueba de nuevo.`];
}

/**
 * Añadir a Apple Wallet (`downloadAppleWalletPass`: el `.pkpass` se abre en el móvil) o guardar en Google Wallet
 * (`saveGoogleWalletPass`: el enlace de Google). Con `negocio` y `token` del QR.
 */
export function useCarteras(ops: OperacionesClienteFinal, alFallar: (n: Nota) => void) {
    const [ocupada, setOcupada] = useState<'apple' | 'google' | null>(null);
    const vivo = useRef(true);
    useEffect(() => {
        vivo.current = true;
        return () => {
            vivo.current = false;
        };
    }, []);
    const anadir = useCallback(
        (cartera: 'apple' | 'google', negocio: string, token: string) => {
            setOcupada(cartera);
            const hecho = cartera === 'apple'
                ? ops.downloadAppleWalletPass(negocio, token).then((archivo) => {
                    navegador.ir(URL.createObjectURL(archivo));
                })
                : ops.saveGoogleWalletPass(negocio, token).then((r) => {
                    navegador.ir(r.save_url);
                });
            hecho
                .catch((e: unknown) => {
                    if (vivo.current) {
                        alFallar(notaCartera(cartera, e));
                    }
                })
                .finally(() => {
                    if (vivo.current) {
                        setOcupada(null);
                    }
                });
        },
        [ops, alFallar],
    );
    return { ocupada, anadir };
}

/**
 * «Guardar el QR» (G602): la imagen del QR en PNG para enseñarla sin conexión. Se dibuja en el navegador con la misma
 * dirección firmada; si el navegador no sabe dibujar, se guarda en SVG.
 */
export function guardarImagenQr(valor: string, nombreArchivo: string): void {
    const qr = qrcode(0, 'M');
    qr.addData(valor);
    qr.make();
    const n = qr.getModuleCount();
    const margen = 4;
    const lado = n + margen * 2;
    const descargar = (url: string, extension: string) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${nombreArchivo}.${extension}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };
    const escala = 12;
    const lienzo = document.createElement('canvas');
    lienzo.width = lado * escala;
    lienzo.height = lado * escala;
    let ctx: CanvasRenderingContext2D | null;
    try {
        ctx = lienzo.getContext('2d');
    } catch {
        ctx = null;
    }
    if (ctx !== null) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, lienzo.width, lienzo.height);
        ctx.fillStyle = '#111';
        for (let f = 0; f < n; f += 1) {
            for (let c = 0; c < n; c += 1) {
                if (qr.isDark(f, c)) {
                    ctx.fillRect((c + margen) * escala, (f + margen) * escala, escala, escala);
                }
            }
        }
        descargar(lienzo.toDataURL('image/png'), 'png');
        return;
    }
    let d = '';
    for (let f = 0; f < n; f += 1) {
        for (let c = 0; c < n; c += 1) {
            if (qr.isDark(f, c)) {
                d += `M${String(c + margen)} ${String(f + margen)}h1v1h-1z`;
            }
        }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${String(lado)} ${String(lado)}" width="${String(lado * escala)}" height="${String(lado * escala)}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${d}" fill="#111"/></svg>`;
    descargar(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, 'svg');
}
